import React, { useEffect, useState } from 'react';
import Layout from './Layout';
import './css/DoctorPage.css';

const DAYS = [
  { id: 1, label: 'Monday' },
  { id: 2, label: 'Tuesday' },
  { id: 3, label: 'Wednesday' },
  { id: 4, label: 'Thursday' },
  { id: 5, label: 'Friday' },
  { id: 6, label: 'Saturday' },
  { id: 7, label: 'Sunday' },
];

const defaultTimetable = () =>
  DAYS.map(d => ({ day_of_week: d.id, time_from: '', time_to: '', is_rest: false }));

const DoctorPage = () => {
  const [user, setUser] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctorToDelete, setDoctorToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [allBranches, setAllBranches] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    phone: '',
    branches: [],       // array of branch pkkeys (numbers)
    timetable: defaultTimetable()
  });

  const totalPages = Math.ceil(filteredDoctors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDoctors = filteredDoctors.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      if (endPage - startPage < maxVisiblePages - 1) startPage = Math.max(1, endPage - maxVisiblePages + 1);
      if (startPage > 1) { pageNumbers.push(1); if (startPage > 2) pageNumbers.push('...'); }
      for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
      if (endPage < totalPages) { if (endPage < totalPages - 1) pageNumbers.push('...'); pageNumbers.push(totalPages); }
    }
    return pageNumbers;
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
    loadDoctors();
    loadBranches();
  }, []);

  useEffect(() => {
    if (filterText.trim() === '') {
      setFilteredDoctors(doctors);
    } else {
      const q = filterText.toLowerCase();
      setFilteredDoctors(doctors.filter(d =>
        d.NAME.toLowerCase().includes(q) ||
        (d.POSITION && d.POSITION.toLowerCase().includes(q)) ||
        (d.phone && d.phone.toLowerCase().includes(q)) ||
        (d.branches && d.branches.toLowerCase().includes(q))
      ));
    }
    setCurrentPage(1);
  }, [filterText, doctors]);

  const loadDoctors = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/doctors');
      const data = await res.json();
      if (data.success) {
        setDoctors(data.doctors);
        setFilteredDoctors(data.doctors);
      } else {
        setError('Failed to load consultants');
      }
    } catch (err) {
      setError('Error loading consultants: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const res = await fetch('/api/doctors/meta/branches');
      const data = await res.json();
      if (data.success) setAllBranches(data.branches);
    } catch (err) {
      console.error('Failed to load branches:', err);
    }
  };

  const handleReset = () => { setFilterText(''); setCurrentPage(1); };
  const handlePageChange = (page) => setCurrentPage(page);

  const emptyForm = () => ({
    name: '',
    position: '',
    phone: '',
    branches: [],
    timetable: defaultTimetable()
  });

  const openAddModal = () => {
    setModalMode('add');
    setFormData(emptyForm());
    setSelectedDoctor(null);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const openEditModal = async (doctor) => {
    setModalMode('edit');
    setSelectedDoctor(doctor);
    setShowModal(true);
    setError('');
    setSuccess('');

    try {
      setLoading(true);
      const res = await fetch(`/api/doctors/${doctor.pkkey}`);
      const data = await res.json();
      if (data.success) {
        const d = data.doctor;
        // Merge existing timetable with default (fill missing days)
        const ttMap = {};
        (d.timetable || []).forEach(r => { ttMap[r.day_of_week] = r; });
        const mergedTT = DAYS.map(day => {
          const existing = ttMap[day.id];
          if (existing) {
            return {
              day_of_week: day.id,
              time_from: existing.time_from ? existing.time_from.substring(0, 5) : '',
              time_to: existing.time_to ? existing.time_to.substring(0, 5) : '',
              is_rest: !!existing.is_rest
            };
          }
          return { day_of_week: day.id, time_from: '', time_to: '', is_rest: false };
        });

        setFormData({
          name: d.name || '',
          position: d.position || '',
          phone: d.phone || '',
          branches: d.branches || [],
          timetable: mergedTT
        });
      } else {
        setError('Failed to load consultant details');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDoctor(null);
    setFormData(emptyForm());
    setError('');
    setSuccess('');
  };

  const openDeleteModal = (doctor, e) => {
    e.stopPropagation();
    setDoctorToDelete(doctor);
    setShowDeleteModal(true);
    setError('');
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDoctorToDelete(null);
    setError('');
  };

  const handleDelete = async () => {
    if (!doctorToDelete) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/doctors/${doctorToDelete.pkkey}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setSuccess('Consultant deleted successfully');
        loadDoctors();
        closeDeleteModal();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Error deleting: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Branch multi-select toggle
  const toggleBranch = (pkkey) => {
    setFormData(prev => {
      const exists = prev.branches.includes(pkkey);
      return {
        ...prev,
        branches: exists
          ? prev.branches.filter(b => b !== pkkey)
          : [...prev.branches, pkkey]
      };
    });
  };

  // Timetable change handlers
  const handleTimetableChange = (dayId, field, value) => {
    setFormData(prev => ({
      ...prev,
      timetable: prev.timetable.map(row =>
        row.day_of_week === dayId ? { ...row, [field]: value } : row
      )
    }));
  };

  const handleRestToggle = (dayId) => {
    setFormData(prev => ({
      ...prev,
      timetable: prev.timetable.map(row =>
        row.day_of_week === dayId
          ? { ...row, is_rest: !row.is_rest, time_from: '', time_to: '' }
          : row
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    const submitData = {
      name: formData.name,
      position: formData.position,
      phone: formData.phone,
      branches: formData.branches,
      timetable: formData.timetable
    };

    try {
      setLoading(true);
      setError('');

      const url = modalMode === 'add'
        ? '/api/doctors/newdoctor'
        : `/api/doctors/savedoctor/${selectedDoctor.pkkey}`;
      const method = modalMode === 'add' ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      const data = await res.json();
      if (data.success) {
        setSuccess(data.message);
        loadDoctors();
        setTimeout(() => closeModal(), 1000);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Layout>
      <div className="homepage-container">
        <div className="homepage-header">
          <h1 className="homepage-title">Consultant Management</h1>
        </div>

        {success && !showModal && !showDeleteModal && (
          <div className="success-message">{success}</div>
        )}

        {/* Filter Section */}
        <div className="filter-section">
          <div className="filter-controls">
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-input"
              placeholder="Filter by Name, Position, Phone, or Branch..."
            />
            <button onClick={handleReset} className="reset-button">Reset</button>
          </div>
          <button onClick={openAddModal} className="new-button">+ New Consultant</button>
        </div>

        {/* Table */}
        <div>
          {loading && !showModal && <div className="loading-message">Loading...</div>}
          {error && !showModal && !showDeleteModal && <div className="error-message">{error}</div>}

          <table className="users-table">
            <thead>
              <tr className="table-header">
                <th>No.</th>
                <th>Name</th>
                <th>Position</th>
                <th>Phone</th>
                <th>Branches</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentDoctors.map((doctor, index) => (
                <tr key={doctor.pkkey} onClick={() => openEditModal(doctor)} className="table-row">
                  <td className="table-cell">{startIndex + index + 1}.</td>
                  <td className="table-cell">{doctor.NAME}</td>
                  <td className="table-cell">{doctor.POSITION || '-'}</td>
                  <td className="table-cell">{doctor.phone || '-'}</td>
                  <td className="table-cell">{doctor.branches || '-'}</td>
                  <td className="table-cell">
                    <button
                      className="delete-button"
                      onClick={(e) => openDeleteModal(doctor, e)}
                      disabled={loading}
                      title="Delete Consultant"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredDoctors.length === 0 && !loading && (
            <div className="no-data-message">No consultants found</div>
          )}

          {filteredDoctors.length > 0 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredDoctors.length)} of {filteredDoctors.length} entries
              </div>
              <div className="pagination-controls">
                <button className="pagination-button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                {getPageNumbers().map((page, idx) => (
                  <React.Fragment key={idx}>
                    {page === '...'
                      ? <span className="pagination-ellipsis">...</span>
                      : <button className={`pagination-button ${page === currentPage ? 'active' : ''}`} onClick={() => handlePageChange(page)}>{page}</button>
                    }
                  </React.Fragment>
                ))}
                <button className="pagination-button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
              </div>
            </div>
          )}
        </div>

        {/* ===== Add / Edit Modal ===== */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content modal-content-wide">
              <h2 className="modal-title">
                {modalMode === 'add' ? 'Add New Consultant' : 'Edit Consultant'}
              </h2>

              {error && <div className="modal-error">{error}</div>}
              {success && <div className="modal-success">{success}</div>}

              <form onSubmit={handleSubmit}>

                {/* Basic Info */}
                <div className="form-section-title">Basic Information</div>

                <div className="form-row">
                  <div className="form-group form-group-flex">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div className="form-group form-group-flex">
                    <label className="form-label">Position</label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className="form-input"
                      autoComplete="off"
                      placeholder="e.g., Cardiologist"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    autoComplete="off"
                  />
                </div>

                {/* Branch Multi-Select */}
                <div className="form-section-title">Branch (multiple allowed)</div>
                <div className="branch-selector">
                  {allBranches.length === 0 && (
                    <span className="no-branch-text">No branches available. Please add branches first.</span>
                  )}
                  {allBranches.map(b => {
                    const selected = formData.branches.includes(b.pkkey);
                    return (
                      <button
                        key={b.pkkey}
                        type="button"
                        className={`branch-chip ${selected ? 'branch-chip-selected' : ''}`}
                        onClick={() => toggleBranch(b.pkkey)}
                      >
                        {b.NAME}
                        {selected && <span className="branch-chip-check"> ✓</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Timetable */}
                <div className="form-section-title">Timetable</div>
                <div className="timetable-grid">
                  <div className="timetable-header-row">
                    <div className="tt-col-day">Day</div>
                    <div className="tt-col-time">From</div>
                    <div className="tt-col-time">To</div>
                    <div className="tt-col-rest">Rest Day</div>
                  </div>
                  {formData.timetable.map(row => {
                    const day = DAYS.find(d => d.id === row.day_of_week);
                    return (
                      <div
                        key={row.day_of_week}
                        className={`timetable-row ${row.is_rest ? 'timetable-row-rest' : ''}`}
                      >
                        <div className="tt-col-day">{day?.label}</div>
                        <div className="tt-col-time">
                          <input
                            type="time"
                            value={row.time_from}
                            disabled={row.is_rest}
                            onChange={(e) => handleTimetableChange(row.day_of_week, 'time_from', e.target.value)}
                            className="form-input tt-time-input"
                            step="60"
                          />
                        </div>
                        <div className="tt-col-time">
                          <input
                            type="time"
                            value={row.time_to}
                            disabled={row.is_rest}
                            onChange={(e) => handleTimetableChange(row.day_of_week, 'time_to', e.target.value)}
                            className="form-input tt-time-input"
                            step="60"
                          />
                        </div>
                        <div className="tt-col-rest">
                          <label className="rest-checkbox-label">
                            <input
                              type="checkbox"
                              checked={row.is_rest}
                              onChange={() => handleRestToggle(row.day_of_week)}
                              className="rest-checkbox"
                            />
                            <span className="toggle-track" />
                            <span className={`rest-label-text ${row.is_rest ? 'rest-label-active' : ''}`}>
                              {row.is_rest ? 'Rest' : 'Working'}
                            </span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="form-actions">
                  <button type="button" onClick={closeModal} className="cancel-button" disabled={loading}>Cancel</button>
                  <button type="submit" className="submit-button" disabled={loading}>
                    {loading ? 'Saving...' : (modalMode === 'add' ? 'Add Consultant' : 'Save Consultant')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2 className="modal-title">Confirm Delete</h2>
              {error && <div className="modal-error">{error}</div>}
              <div className="delete-confirmation">
                <p>Are you sure you want to delete this consultant?</p>
                <div className="client-details">
                  <strong>Name:</strong> {doctorToDelete?.NAME}<br />
                  <strong>Position:</strong> {doctorToDelete?.POSITION || 'N/A'}<br />
                  <strong>Phone:</strong> {doctorToDelete?.phone || 'N/A'}
                </div>
                <p className="warning-text">This action cannot be undone. Branch assignments and timetable will also be deleted.</p>
              </div>
              <div className="form-actions">
                <button type="button" onClick={closeDeleteModal} className="cancel-button" disabled={loading}>Cancel</button>
                <button type="button" onClick={handleDelete} className="delete-confirm-button" disabled={loading}>
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default DoctorPage;